import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Client } from './client';
import { Observable } from 'rxjs/Observable';
import { pluck, mergeMap, tap } from 'rxjs/operators';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { of } from 'rxjs/observable/of';
import { PassengerBonus } from '@app/fare-editor/models/passenger-apps.model';

interface ProductToSave {
  product_id: string;
  enabled_inputs: string[];
}

@Injectable()
export class PassengerAppService {
  constructor(private http: HttpClient, private client: Client) {}

  getFilteredProducts(service_id): Observable<any> {
    return this.http.get(this.client.getFaresUrl() + '/passenger-apps' + '?service=' + service_id);
  }

  getListOfProductsByAppId(appId: string): Observable<any> {
    return this.http.get(`${this.client.getProductsList()}/products?id=${appId}`);
  }

  patchOrderOfProduct(appId: string, productId: string, isDefault: boolean, order: number): Observable<any> {
    return this.http.put(`${this.client.getProductsList()}/products`, {
      passenger_app_id: appId,
      product_id: productId,
      is_default: isDefault,
      display_order: order
    });
  }

  patchDefaultVehicleClassOfProduct(productId: string, vehicleClassId: string): Observable<any> {
    return this.http.put(this.client.getFaresUrl(), {
      id: productId,
      vehicle_class_id: vehicleClassId
    });
  }

  // pass bonuses

  getListOfPassBonuses(serviceId: number): Observable<any> {
    return this.http.get(`${this.client.getPassengerBonusesListUrl()}?service_id=${serviceId}`);
  }

  updateBonus(event, serviceId, value, paymentType): Observable<any> {
    const arrayOfProductsToSave = this.generateArrayOfProducts(event.products);
    const useProductFilter = arrayOfProductsToSave.length > 0;
    return this.putBonus(serviceId, event.name, value, event.id, paymentType, useProductFilter).pipe(
      mergeMap((updatedBonus: PassengerBonus) => {
        let reqs = [];

        updatedBonus.products.forEach(oldProd => {
          if (!arrayOfProductsToSave.some(prod => prod.product_id === oldProd.product_id)) {
            reqs.push(this.deleteProduct(oldProd.id));
          }
        });
        arrayOfProductsToSave.forEach(prod => {
          const prodExist = updatedBonus.products.find(item => item.product_id === prod.product_id);
          if (!prodExist) {
            reqs.push(this.postProduct(updatedBonus.id, prod.product_id, prod.enabled_inputs));
          } else if (prodExist.enabled_inputs !== prod.enabled_inputs) {
            reqs.push(this.putProduct(prodExist.id, prod.enabled_inputs));
          }
        });
        if (reqs.length > 0) {
          return forkJoin([...reqs, of(updatedBonus)]);
        } else {
          return of([updatedBonus]);
        }
      }),
      mergeMap(this.creationBonusAssembly)
    );
  }

  createBonus(event, serviceId, value, paymentType): Observable<any> {
    const arrayOfProductsToSave = this.generateArrayOfProducts(event.products);
    const useProductFilter = arrayOfProductsToSave.length > 0;
    return this.postBonus(serviceId, event.name, value, paymentType, useProductFilter).pipe(
      mergeMap(
        (createdBonus: PassengerBonus): Observable<Array<any>> => {
          if (arrayOfProductsToSave.length > 0) {
            // && !arrayOfProductsToSave.some(item => item.product_id === 'all')
            const reqs = this.getListOfPostProductReqs(arrayOfProductsToSave, createdBonus.id);
            return forkJoin([...reqs, of(createdBonus)]);
          } else {
            return of([createdBonus]);
          }
          // lastItem always saved bonus
        }
      ),
      mergeMap(this.creationBonusAssembly)
    );
  }

  removeBonus(id: string): Observable<any> {
    return this.http.delete(`${this.client.getPassengerBonusesListUrl()}?id=${id}`);
  }

  private deleteProduct(id: string): Observable<any> {
    return this.http.delete(`${this.client.getPassengerBonusesListUrl()}/products?id=${id}`);
  }

  private creationBonusAssembly = (infoArray: any): Observable<PassengerBonus> => {
    if (infoArray.length > 1) {
      return of(this.mergePassProductsAndBonus(infoArray));
    } else {
      return of(infoArray[0]);
    }
  };

  private mergePassProductsAndBonus(productsAndBonus: any[]): PassengerBonus {
    return {
      ...productsAndBonus[productsAndBonus.length - 1],
      products: productsAndBonus.filter(item => Boolean(item) && Boolean(item.product_id))
    };
  }

  private getListOfPostProductReqs(arrayOfProductsToSave, bonusId: string): Array<Observable<any>> {
    return arrayOfProductsToSave.map((prodToSave: ProductToSave) => {
      return this.postProduct(bonusId, prodToSave.product_id, prodToSave.enabled_inputs);
    });
  }

  private postProduct(bonusId: string, productId: string, enabledInputs: string[]): Observable<any> {
    return this.http
      .post(`${this.client.getPassengerBonusesListUrl()}/products`, {
        bonus_id: bonusId,
        product_id: productId,
        enabled_inputs: enabledInputs
      })
      .pipe(pluck('data'));
  }

  private putProduct(id: string, enabledInputs: string[]): Observable<any> {
    return this.http
      .put(`${this.client.getPassengerBonusesListUrl()}/products`, {
        id,
        enabled_inputs: enabledInputs
      })
      .pipe(pluck('data'));
  }

  private postBonus(serviceId: number, name: string, value: string, paymentType: any, useProductFilter: boolean): Observable<any> {
    return this.http
      .post(this.client.getPassengerBonusesListUrl(), {
        service_id: serviceId,
        name,
        value,
        payment_type: paymentType,
        use_product_filter: useProductFilter
      })
      .pipe(pluck('data'));
  }

  private putBonus(serviceId: number, name: string, value: string, id: string, paymentType: any, useProductFilter: boolean): Observable<any> {
    return this.http
      .put(this.client.getPassengerBonusesListUrl(), {
        id,
        service_id: serviceId,
        name,
        value,
        payment_type: paymentType,
        use_product_filter: useProductFilter
      })
      .pipe(pluck('data'));
  }

  private generateArrayOfProducts(products: string[]): ProductToSave[] {
    let productsToSave: ProductToSave[] = [];

    if (products.some(item => item === 'all')) {
      return [];
    }

    products.forEach((val: string) => {
      const typeOfVal = val.split('-')[0];
      if (typeOfVal !== 'app' && typeOfVal !== 'telephony') {
        productsToSave.push({ product_id: val, enabled_inputs: [] });
      }
    });

    productsToSave = productsToSave.map((prod: ProductToSave) => {
      return { product_id: prod.product_id, enabled_inputs: this.getEnabledInputsOfProduct(prod.product_id, products) };
    });

    return productsToSave;
  }

  private getEnabledInputsOfProduct(productId: string, prods: string[]): string[] {
    return prods.filter((prod: string) => prod.indexOf(productId) > 0).map(filteredItem => filteredItem.split('-')[0]);
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/passenger-app.service.ts